import React, { useState, useEffect } from "react";
import { X, Smartphone, Zap, WifiOff, Sparkles } from "lucide-react";
import { analytics } from "../utils/analytics";

/**
 * A friendly, non-intrusive banner encouraging users to add
 * Isaiah's World to their home screen, explained in plain language
 * with clear benefits, no technical jargon.
 *
 * Props:
 *   canShow       , from useInstallPrompt
 *   onInstall     , triggers the native install dialog
 *   onDismiss     , hides the banner for 14 days
 */
export default function InstallPrompt({ canShow, onInstall, onDismiss, onNeverShow }) {
  const [visible, setVisible] = useState(false);

  // Animate in after a short delay (don't startle the user)
  useEffect(() => {
    if (!canShow) { setVisible(false); return; }
    const timer = setTimeout(() => {
      setVisible(true);
      analytics.installPromptShown();
    }, 2000);
    return () => clearTimeout(timer);
  }, [canShow]);

  if (!canShow) return null;

  return (
    <div
      data-testid="install-prompt"
      className={`fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[9998] transition-all duration-500 ${
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-8 opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.97) 0%, rgba(30,27,75,0.97) 100%)",
          border: "1px solid rgba(139,92,246,0.3)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(139,92,246,0.1)",
          
        }}
      >
        {/* Dismiss button */}
        <button
          data-testid="install-prompt-dismiss"
          onClick={onDismiss}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
          aria-label="Maybe later"
        >
          <X size={14} className="text-white/50" />
        </button>

        <div className="p-5 sm:p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center shrink-0">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold text-base leading-tight">
                Add to Your Home Screen
              </h3>
              <p className="text-white/50 text-xs">
                One tap to open, just like a real app
              </p>
            </div>
          </div>

          {/* Benefits, plain language, no jargon */}
          <div className="space-y-2.5 mb-5">
            <Benefit
              icon={<Smartphone size={14} />}
              text="Opens full-screen from your home screen, no browser needed"
            />
            <Benefit
              icon={<Zap size={14} />}
              text="Loads instantly, even on slow connections"
            />
            <Benefit
              icon={<WifiOff size={14} />}
              text="Works offline, keep playing without internet"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              data-testid="install-prompt-accept"
              onClick={() => { analytics.installAccepted(); onInstall(); }}
              className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: "linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)",
                boxShadow: "0 4px 16px rgba(139,92,246,0.3)",
              }}
            >
              Add to Home Screen
            </button>
            <button
              data-testid="install-prompt-later"
              onClick={() => { analytics.installDismissed(); onDismiss(); }}
              className="py-2.5 px-4 rounded-xl text-sm text-white/40 hover:text-white/60 transition-colors"
            >
              Not now
            </button>
          </div>

          {/* Permanent opt-out */}
          <button
            data-testid="install-prompt-never"
            onClick={() => { analytics.installDismissed(); (onNeverShow || onDismiss)(); }}
            className="w-full mt-2 py-1.5 rounded-lg text-[12px] text-white/30 hover:text-white/55 transition-colors"
          >
            Don&apos;t remind me again
          </button>

          {/* Reassurance */}
          <p className="text-white/30 text-[11px] text-center mt-3">
            Free &middot; No app store &middot; No storage space used
          </p>
        </div>
      </div>
    </div>
  );
}

function Benefit({ icon, text }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-5 h-5 rounded-md bg-violet-500/10 border border-violet-500/20 flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-violet-400">{icon}</span>
      </div>
      <span className="text-white/70 text-sm leading-snug">{text}</span>
    </div>
  );
}
